import React, { useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useDataSourceProfile from '@afosto/utils-react/dataSource/useDataSourceProfile';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import { useAppState } from '@afosto/app-components-react/AppProvider';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import FulfilmentLocationFilter from '../../components/FulfilmentLocationFilter';
import FulfilmentLocationRenderer from '../../components/FulfilmentLocationRenderer';
import HandlingListCard from './HandlingListCard';
import HandlingListCardSkeleton from './HandlingListCard/HandlingListCardSkeleton';
import ShipmentFilter from '../../components/ShipmentFilter';
import ShipmentRenderer from '../../components/ShipmentRenderer';
import SwitchLocationsDialog from '../../components/SwitchLocationsDialog';
import Page from '@afosto/app-components-react/Page';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import createDataGridProfileView from '@afosto/utils-react/datagrid/createDataGridProfileView';
import createDefaultView from '@afosto/app-components-react/DataSource/modules/views/utils';
import filterOptionsQuery from '@afosto/utils-react/datagrid/filterOptionsQuery';
import searchQuery from '@afosto/utils-react/datagrid/searchQuery';
import updateDataGridDefaultView from '@afosto/utils-react/datagrid/updateDataGridDefaultView';
import updateDataGridProfilePositions from '@afosto/utils-react/datagrid/updateDataGridProfilePositions';
import updateDataGridProfileView from '@afosto/utils-react/datagrid/updateDataGridProfileView';
import { fulfilmentLocationFormatter } from '../../utils/formatters';
import { HandlingListFormatter, statusFormatter, shipmentsFormatter } from './formatters';
import translations from './translations';

const HANDLING_LISTS_PROFILE_CODE = 'lcs';
const HANDLING_LISTS_PROFILE_MODEL = 'lists';
const HANDLING_LISTS_PROFILE_PATH = 'lcs.handling';
const HANDLING_LISTS_PROFILE_NAMESPACE = 'default';

const DataListGridContainer = props => <Grid container spacing={1} {...props} />;

const HandlingLists = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:handling:read',
    'lcs:locations:read',
  ]);

  const intl = useIntl();
  const sessionManager = useSessionManager();
  const { settings } = useAppState();
  const { locations = [] } = settings;
  const [showSwitchLocationsDialog, setShowSwitchLocationsDialog] = useState(false);

  const searchIdentifier = {
    code: HANDLING_LISTS_PROFILE_CODE,
    model: HANDLING_LISTS_PROFILE_MODEL,
    namespace: HANDLING_LISTS_PROFILE_NAMESPACE,
  };
  const fixedProperties = [
    {
      key: 'number',
      name: intl.formatMessage({
        id: `dataGrid.columns.${HANDLING_LISTS_PROFILE_MODEL}.number`,
        defaultMessage: 'Pick list number',
        description: 'The column name for column number',
      }),
      type: 'handlingList',
      sortable: true,
    },
  ];
  const defaultProperties = [
    {
      key: 'location',
      name: intl.formatMessage({
        id: `dataGrid.columns.${HANDLING_LISTS_PROFILE_MODEL}.location`,
        defaultMessage: 'Location',
        description: 'The column name for column location',
      }),
      type: 'fulfilmentLocation',
      isSortable: false,
    },
    {
      key: 'shipment_ids',
      name: intl.formatMessage({
        id: `dataGrid.columns.${HANDLING_LISTS_PROFILE_MODEL}.shipments`,
        defaultMessage: 'Shipments',
        description: 'The column name for column shipments',
      }),
      type: 'shipments',
      isSortable: false,
    },
    {
      key: 'is_outbound',
      name: intl.formatMessage({
        id: `dataGrid.columns.${HANDLING_LISTS_PROFILE_MODEL}.isOutbound`,
        defaultMessage: 'Outbound',
        description: 'The column name for column is outbound',
      }),
      type: 'boolean',
      isSortable: false,
    },
    {
      key: 'status',
      name: intl.formatMessage({
        id: `dataGrid.columns.${HANDLING_LISTS_PROFILE_MODEL}.status`,
        defaultMessage: 'Status',
        description: 'The column name for column status',
      }),
      type: 'status',
      isSortable: false,
    },
    {
      key: 'due_at',
      name: intl.formatMessage({
        id: `dataGrid.columns.${HANDLING_LISTS_PROFILE_MODEL}.dueAt`,
        defaultMessage: 'Due at',
        description: 'The column name for column due at',
      }),
      type: 'datetime',
    },
    {
      key: 'created_at',
      name: intl.formatMessage({
        id: `dataGrid.columns.${HANDLING_LISTS_PROFILE_MODEL}.createdAt`,
        defaultMessage: 'Created at',
        description: 'The column name for column created at',
      }),
      type: 'datetime',
    },
  ];
  const { isFetchingProfile, filters, views, properties: filterProperties } = useDataSourceProfile({
    ...searchIdentifier,
    profilePath: HANDLING_LISTS_PROFILE_PATH,
    filterTypes: {
      contact_id: 'contact',
      location: 'fulfilmentLocation',
      method_id: 'shippingMethod',
      shipment_ids: 'shipment',
      skus: 'sku',
    },
    ignoreFilters: ['indexed_at', 'list_id', 'reference', 'shipment_numbers'],
    ignoreProperties: [
      'list_id',
      'shipment_numbers',
      ...defaultProperties.map(property => property.key),
    ],
    ignoreSorting: [
      'array',
      'boolean',
      'sku',
      'shipment',
      'shippingMethod',
      'fulfilmentLocation',
      'contact',
    ],
    propertyTypes: {
      skus: 'array',
    },
  });

  const dataSource = useDataGrid(searchQuery(searchIdentifier), {
    gridOptions: {
      columnSelection: true,
      copyGridUrl: true,
      filterSelection: true,
      advancedSort: true,
      viewsSelection: true,
    },
    options: {
      dataSourceUrl: true,
      isLoadingData: isFetchingProfile,
      storeStateKey: 'handling_lists',
    },
    modules: ['dataProperties', 'pagination', 'filters', 'sorting', 'views', 'search'],
    dataProperties: {
      properties: [...defaultProperties, ...filterProperties, ...fixedProperties],
      fixedProperties,
    },
    views: {
      storeViewKey: 'handling_lists_view',
      viewShortcuts: true,
      canCreateViewForTenant:
        sessionManager && sessionManager.hasPermissions(['lcs:handling:write']),
      defaultView: createDefaultView(intl, {
        properties: [...defaultProperties],
        filters: [
          {
            key: 'is_finished',
            operator: 'in',
            value: [false],
            type: 'boolean',
          },
          {
            key: 'is_in_progress',
            operator: 'in',
            value: [false],
            type: 'boolean',
          },
          {
            key: 'is_outbound',
            operator: 'in',
            value: [true],
            type: 'boolean',
          },
        ],
        sort: [
          {
            key: 'due_at',
            direction: 'asc',
          },
        ],
      }),
      views,
      onAddView: (view, otherViews) =>
        createDataGridProfileView(HANDLING_LISTS_PROFILE_PATH)(view, { views: otherViews }),
      onUpdateView: view => updateDataGridProfileView(HANDLING_LISTS_PROFILE_PATH)(view),
      onDeleteView: view => updateDataGridProfileView(HANDLING_LISTS_PROFILE_PATH)(view),
      onRepositionView: repositionedViews =>
        updateDataGridProfilePositions(HANDLING_LISTS_PROFILE_PATH)(repositionedViews),
      onChangeDefaultView: updatedViews =>
        updateDataGridDefaultView(HANDLING_LISTS_PROFILE_PATH)(updatedViews),
    },
    filters: {
      loadFilterOptions: filterOptionsQuery(searchIdentifier),
      filters,
      fixedFilters: [
        ...(locations?.length
          ? [{ key: 'location', operator: 'in', value: locations.map(locationId => locationId) }]
          : []),
      ],
    },
  });

  /**
   * Render a handling list.
   * @param {object} handlingList
   * @returns {*}
   */
  const renderHandlingList = handlingList => (
    <Grid key={handlingList.id} item xs={12}>
      <HandlingListCard handlingList={handlingList} />
      <Divider />
    </Grid>
  );

  /**
   * Render the skeleton for the handling list in the data list.
   * @param {string} key
   * @returns {*}
   */
  const renderHandlingListSkeleton = key => (
    <Grid key={key} xs={12} item>
      <HandlingListCardSkeleton />
      <Divider />
    </Grid>
  );

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  const filterFormatters = {
    fulfilmentLocation: FulfilmentLocationFilter,
    shipment: ShipmentFilter,
  };

  const filterRenderers = {
    fulfilmentLocation: FulfilmentLocationRenderer,
    shipment: ShipmentRenderer,
  };

  return (
    <>
      <Page
        title={intl.formatMessage(translations.title)}
        secondaryActions={[
          {
            text: intl.formatMessage(translations.secondaryActions.switchLocations),
            action: () => {
              setShowSwitchLocationsDialog(true);
            },
            shortCut: true,
            icon: ['fal', 'exchange'],
          },
        ]}
        fullWidth
      >
        <Hidden implementation="js" mdUp>
          <DataList
            dataSource={dataSource}
            renderItem={renderHandlingList}
            renderSkeletonItem={renderHandlingListSkeleton}
            component={DataListGridContainer}
            filterFormatters={filterFormatters}
            filterRenderers={filterRenderers}
          />
        </Hidden>
        <Hidden implementation="js" smDown>
          <DataGrid
            dataSource={dataSource}
            formatters={{
              handlingList: HandlingListFormatter,
              status: statusFormatter,
              shipments: shipmentsFormatter,
              fulfilmentLocation: fulfilmentLocationFormatter,
            }}
            filterFormatters={filterFormatters}
            filterRenderers={filterRenderers}
          />
        </Hidden>
      </Page>
      <SwitchLocationsDialog
        isOpen={showSwitchLocationsDialog}
        onCancel={() => setShowSwitchLocationsDialog(false)}
        onSwitch={() => setShowSwitchLocationsDialog(false)}
      />
    </>
  );
};

export default HandlingLists;
